/**
 * CATEGORY MANAGER
 * 
 * Admin component for managing donation categories.
 * Categories define where donations can be allocated.
 * 
 * @module Donations/Components/Settings
 */

import React, { useState, useEffect } from 'react';
import {
    Plus,
    Edit,
    Trash2,
    Save,
    X,
    Eye,
    EyeOff,
    RefreshCw,
    AlertCircle,
    CheckCircle,
    Loader2,
    Landmark,
    FileText,
    Sprout,
    TrendingUp,
    Leaf,
    Heart,
    PlusCircle,
    Building2,
    Microscope,
    Radio,
    GraduationCap,
    HeartPulse,
    Siren,
    Globe,
    Users,
    HandCoins,
    BookOpen,
    Mic,
    Target,
    Shield,
    Award,
    Zap,
    Coffee,
    Droplet,
    Trees,
    Sun,
    Mountain,
    Waves,
    Flower2,
    Cloud,
    Wind,
    Flame,
    Gem,
    Star,
    Compass,
    MapPin,
    Eye as EyeIcon
} from 'lucide-react';

// Icon mapping - maps icon names to Lucide components
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    Landmark,
    FileText,
    Sprout,
    TrendingUp,
    Leaf,
    Heart,
    PlusCircle,
    Building2,
    Microscope,
    Radio,
    GraduationCap,
    HeartPulse,
    Siren,
    Globe,
    Users,
    HandCoins,
    BookOpen,
    Mic,
    Target,
    Shield,
    Award,
    Zap,
    Coffee,
    Droplet,
    Trees,
    Sun,
    Mountain,
    Waves,
    Flower2,
    Cloud,
    Wind,
    Flame,
    Gem,
    Star,
    Compass,
    MapPin,
    EyeIcon
};

interface Category {
    _id?: string;
    name: string;
    slug: string;
    description: string;
    icon: string;  // Name of the Lucide icon
    color: string;
    isActive: boolean;
    sortOrder: number;
    createdAt?: Date;
    updatedAt?: Date;
}

interface CategoryManagerProps {
    onSave?: (categories: Category[]) => void;
}

// Available Lucide icons for categories
const AVAILABLE_ICONS = [
    { name: 'Landmark', icon: Landmark },
    { name: 'FileText', icon: FileText },
    { name: 'Sprout', icon: Sprout },
    { name: 'TrendingUp', icon: TrendingUp },
    { name: 'Leaf', icon: Leaf },
    { name: 'Heart', icon: Heart },
    { name: 'Building2', icon: Building2 },
    { name: 'Microscope', icon: Microscope },
    { name: 'Radio', icon: Radio },
    { name: 'GraduationCap', icon: GraduationCap },
    { name: 'HeartPulse', icon: HeartPulse },
    { name: 'Siren', icon: Siren },
    { name: 'Globe', icon: Globe },
    { name: 'Users', icon: Users },
    { name: 'HandCoins', icon: HandCoins },
    { name: 'BookOpen', icon: BookOpen },
    { name: 'Mic', icon: Mic },
    { name: 'Target', icon: Target },
    { name: 'Shield', icon: Shield },
    { name: 'Award', icon: Award },
    { name: 'Zap', icon: Zap },
    { name: 'Coffee', icon: Coffee },
    { name: 'Droplet', icon: Droplet },
    { name: 'Trees', icon: Trees },
    { name: 'Sun', icon: Sun },
    { name: 'Mountain', icon: Mountain },
    { name: 'Waves', icon: Waves },
    { name: 'Flower2', icon: Flower2 },
    { name: 'Cloud', icon: Cloud },
    { name: 'Wind', icon: Wind },
    { name: 'Flame', icon: Flame },
    { name: 'Gem', icon: Gem },
    { name: 'Star', icon: Star },
    { name: 'Compass', icon: Compass },
    { name: 'MapPin', icon: MapPin }
];

const DEFAULT_CATEGORIES: Category[] = [
    { 
        name: 'Governance & Accountability', 
        slug: 'governance', 
        description: 'Strengthening democratic processes and accountability', 
        icon: 'Landmark', 
        color: '#8B5CF6', 
        isActive: true, 
        sortOrder: 1 
    },
    { 
        name: 'Policy Analysis & Advocacy', 
        slug: 'policy-advocacy', 
        description: 'Research and advocacy for progressive policies', 
        icon: 'FileText', 
        color: '#3B82F6', 
        isActive: true, 
        sortOrder: 2 
    },
    { 
        name: 'Agricultural Support', 
        slug: 'agriculture', 
        description: 'Supporting small-scale farmers and food security', 
        icon: 'Sprout', 
        color: '#10B981', 
        isActive: true, 
        sortOrder: 3 
    },
    { 
        name: 'Socio-Economic Empowerment', 
        slug: 'socio-economic', 
        description: 'Economic empowerment and livelihood programs', 
        icon: 'TrendingUp', 
        color: '#F59E0B', 
        isActive: true, 
        sortOrder: 4 
    },
    { 
        name: 'Environmental Sustainability', 
        slug: 'environment', 
        description: 'Conservation and sustainable practices', 
        icon: 'Leaf', 
        color: '#22C55E', 
        isActive: true, 
        sortOrder: 5 
    },
    { 
        name: 'Cultural & Remembrance', 
        slug: 'cultural-remembrance', 
        description: 'Cultural heritage and remembrance programs', 
        icon: 'Heart', 
        color: '#EC4899', 
        isActive: true, 
        sortOrder: 6 
    },
    { 
        name: 'General Fund', 
        slug: 'general', 
        description: 'Support our most critical needs', 
        icon: 'PlusCircle', 
        color: '#6B7280', 
        isActive: true, 
        sortOrder: 7 
    }
];

export const CategoryManager: React.FC<CategoryManagerProps> = ({ onSave }) => {
    const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [formData, setFormData] = useState<Partial<Category>>({});

    // Load categories from API (TODO: Replace with actual API call)
    useEffect(() => {
        // In production, fetch from API
    }, []);

    const handleAdd = () => {
        setIsAdding(true);
        setFormData({ 
            name: '', 
            slug: '', 
            description: '', 
            icon: 'PlusCircle', 
            color: '#6B7280', 
            isActive: true 
        });
        setError(null);
    };

    const handleEdit = (category: Category) => {
        setEditingId(category._id || category.name);
        setFormData(category);
        setError(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setIsAdding(false);
        setFormData({});
        setError(null);
    };

    const validateForm = (): boolean => {
        if (!formData.name?.trim()) {
            setError('Category name is required');
            return false;
        }
        if (!formData.slug?.trim()) {
            setError('Slug is required');
            return false;
        }
        if (!formData.description?.trim()) {
            setError('Description is required');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            if (isAdding) {
                const newCategory = {
                    ...formData,
                    _id: `temp-${Date.now()}`,
                    sortOrder: categories.length + 1
                } as Category;
                setCategories([...categories, newCategory]);
                setSuccess('Category added successfully!');
            } else if (editingId) {
                setCategories(categories.map(c => 
                    (c._id === editingId || c.name === editingId) 
                        ? { ...c, ...formData } as Category 
                        : c
                ));
                setSuccess('Category updated successfully!');
            }
            
            if (onSave) {
                onSave(categories);
            }

            handleCancel();
        } catch (err) {
            setError('Failed to save category. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this category? This will affect existing donations.')) {
            return;
        }

        setLoading(true);
        try {
            setCategories(categories.filter(c => c._id !== id && c.name !== id));
            setSuccess('Category deleted successfully!');
        } catch (err) {
            setError('Failed to delete category. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleActive = (id: string) => {
        setCategories(categories.map(c => 
            (c._id === id || c.name === id) 
                ? { ...c, isActive: !c.isActive } 
                : c
        ));
    };

    const moveUp = (index: number) => {
        if (index === 0) return;
        const newCategories = [...categories];
        [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
        setCategories(newCategories);
    };

    const moveDown = (index: number) => {
        if (index === categories.length - 1) return;
        const newCategories = [...categories];
        [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
        setCategories(newCategories);
    };

    // Helper to render the icon
    const renderIcon = (iconName: string, size: number = 18) => {
        const IconComponent = ICON_MAP[iconName];
        if (!IconComponent) {
            return <PlusCircle size={size} />; // Fallback icon
        }
        return <IconComponent size={size} />;
    };

    if (loading && categories.length === 0) {
        return (
            <div className="loading-state">
                <Loader2 size={32} className="spinner" />
                <p>Loading categories...</p>
            </div>
        );
    }

    return (
        <div className="category-manager">
            <div className="manager-header">
                <div className="header-left">
                    <h3>Donation Categories</h3>
                    <span className="count-badge">{categories.length} categories</span>
                </div>
                <button className="btn-primary" onClick={handleAdd} disabled={loading || isAdding}>
                    <Plus size={16} />
                    Add Category
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <AlertCircle size={16} />
                    {error}
                    <button className="close-error" onClick={() => setError(null)}>✕</button>
                </div>
            )}

            {success && (
                <div className="success-message">
                    <CheckCircle size={16} />
                    {success}
                    <button className="close-success" onClick={() => setSuccess(null)}>✕</button>
                </div>
            )}

            <div className="category-list">
                {categories.map((category, index) => (
                    <div key={category._id || category.name} className={`category-item ${!category.isActive ? 'inactive' : ''}`}>
                        {editingId === (category._id || category.name) ? (
                            <div className="category-edit-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Category Name</label>
                                        <input
                                            type="text"
                                            value={formData.name || ''}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="e.g., Governance & Accountability"
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Slug</label>
                                        <input
                                            type="text"
                                            value={formData.slug || ''}
                                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                            placeholder="e.g., governance"
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Description</label>
                                        <input
                                            type="text"
                                            value={formData.description || ''}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Brief description"
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Icon</label>
                                        <select
                                            value={formData.icon || 'PlusCircle'}
                                            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                            disabled={loading}
                                        >
                                            {AVAILABLE_ICONS.map((icon) => (
                                                <option key={icon.name} value={icon.name}>
                                                    <icon.icon size={14} /> {icon.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Color</label>
                                        <input
                                            type="color"
                                            value={formData.color || '#6B7280'}
                                            onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group checkbox-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formData.isActive !== false}
                                                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                                disabled={loading}
                                            />
                                            Active
                                        </label>
                                    </div>
                                </div>
                                <div className="edit-actions">
                                    <button className="btn-primary" onClick={handleSave} disabled={loading}>
                                        {loading ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
                                        {loading ? 'Saving...' : 'Save'}
                                    </button>
                                    <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
                                        <X size={14} />
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="category-info">
                                    <div className="category-color" style={{ backgroundColor: category.color }} />
                                    <div className="category-icon-wrapper">
                                        {renderIcon(category.icon || 'PlusCircle', 20)}
                                    </div>
                                    <div className="category-details">
                                        <span className="category-name">{category.name}</span>
                                        <span className="category-slug">/{category.slug}</span>
                                        <span className="category-description">{category.description}</span>
                                    </div>
                                    <span className={`category-status ${category.isActive ? 'active' : 'inactive'}`}>
                                        {category.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="category-actions">
                                    <button 
                                        className="action-btn" 
                                        onClick={() => toggleActive(category._id || category.name)}
                                        title={category.isActive ? 'Deactivate' : 'Activate'}
                                        disabled={loading}
                                    >
                                        {category.isActive ? <Eye size={14} /> : <EyeOff size={14} />}
                                    </button>
                                    <button className="action-btn" onClick={() => moveUp(index)} title="Move up" disabled={loading}>
                                        ↑
                                    </button>
                                    <button className="action-btn" onClick={() => moveDown(index)} title="Move down" disabled={loading}>
                                        ↓
                                    </button>
                                    <button className="action-btn" onClick={() => handleEdit(category)} title="Edit" disabled={loading}>
                                        <Edit size={14} />
                                    </button>
                                    <button 
                                        className="action-btn danger" 
                                        onClick={() => handleDelete(category._id || category.name)}
                                        title="Delete"
                                        disabled={loading}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {isAdding && (
                <div className="category-edit-form add-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Category Name</label>
                            <input
                                type="text"
                                value={formData.name || ''}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Governance & Accountability"
                                disabled={loading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Slug</label>
                            <input
                                type="text"
                                value={formData.slug || ''}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                placeholder="e.g., governance"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Description</label>
                            <input
                                type="text"
                                value={formData.description || ''}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Brief description"
                                disabled={loading}
                            />
                        </div>
                        <div className="form-group">
                            <label>Icon</label>
                            <select
                                value={formData.icon || 'PlusCircle'}
                                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                                disabled={loading}
                            >
                                {AVAILABLE_ICONS.map((icon) => (
                                    <option key={icon.name} value={icon.name}>
                                        {icon.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Color</label>
                            <input
                                type="color"
                                value={formData.color || '#6B7280'}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                disabled={loading}
                            />
                        </div>
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={formData.isActive !== false}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    disabled={loading}
                                />
                                Active
                            </label>
                        </div>
                    </div>
                    <div className="edit-actions">
                        <button className="btn-primary" onClick={handleSave} disabled={loading}>
                            {loading ? <Loader2 size={14} className="spinner" /> : <Save size={14} />}
                            {loading ? 'Saving...' : 'Add Category'}
                        </button>
                        <button className="btn-secondary" onClick={handleCancel} disabled={loading}>
                            <X size={14} />
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryManager;
